import { getOrCreateMainLogger } from "../logger/main";
import { createWindowRequest } from "./MainWindow";
import { mainStore, windowsIndex } from "./mainStore";
import { mainBus } from "./MainBus";
import { MainSystem } from "./MainSystem";
import { permissionsManager } from "./PermissionsManager";
import { ProtocolHandler } from "./protocolHandler";
import { DownloadManager } from "./DownloadManager";
import { maybeModifyRequestHeaders, maybeModifyResponseHeaders } from "./contentSecurity";
import { getWindowIdentity, getWindowIdentityForWindowName } from "./getWindowIdentity";
import { SplashScreen } from "./SplashScreen";
import { Manifest } from "../types/types";
import { ipcMain, App, screen, powerMonitor, session, OnHeadersReceivedListenerDetails } from "electron";
import { ScreenCapture } from "./ScreenCapture";
import { listenForWindowStreamSubscriber } from "./WindowEventStream";
import MonitorInfo from "./MonitorInfo";

const logger = getOrCreateMainLogger();

const debugLevelErrors = ["net::ERR_ABORTED", "net::ERR_CACHE_MISS", "net::ERR_CONNECTION_REFUSED", "net::ERR_FAILED"];

/**
 * Electron windows with downloaded file attachments stay open by default,
 * so close them if they have content-disposition: attachment
 * @param {object} details Response headers
 */
const maybeCloseAttachmentWindows = (details: OnHeadersReceivedListenerDetails) => {
	const { webContentsId } = details;
	const responseHeaders = details.responseHeaders ?? {};
	const headerCDKey = "Content-Disposition";
	const keys = Object.keys(responseHeaders);
	if (
		webContentsId &&
		keys.some(
			(key) => key.toLowerCase() === headerCDKey.toLowerCase() && responseHeaders[key][0].includes("attachment")
		)
	) {
		logger.log(`Closing window with content-distribution:attachment for url: ${details.url}`);
		const { mainWindow } = windowsIndex.byWebContentsId[webContentsId] ?? {};

		// Make sure that the window is just for the downloaded attachment
		if (details.url == mainWindow.url) mainWindow.close();
	}
};

/**
 * Test the passed url against the passed regex pattern.
 * This function throws if the passed regex string is invalid.
 */
const isPermittedURL = (pattern: string, url: string) => {
	const regex = new RegExp(pattern);
	return regex.test(url);
};

/**
 * Invoked before any http(s) request to check against any preconfigured feaURLWhitelist
 */
const maybePreventLoadDueToWhiteList = (manifest: Manifest, request: any, callback: Function) => {
	logger.verbose("main->_onBeforeRequest", request.url);
	const localhostRegex = /^https?:\/\/localhost:\d+/i;
	const devtools = /^chrome-devtools.*/i;

	const { feaURLWhitelist } = manifest.finsemble;
	const { url } = request;
	let cancel = false;
	// Ignore when it's a local finsemble request
	if (devtools.test(url) || localhostRegex.test(url)) {
		callback({});
		return;
	}
	// Care only about mainFrame requests for now
	if (request.resourceType !== "mainFrame") {
		callback({});
		return;
	}
	// Here we decide whether we need to cancel the
	// request or not based on our given regex
	if (feaURLWhitelist) {
		try {
			cancel = !isPermittedURL(feaURLWhitelist, url);
		} catch (error: any) {
			logger.error(`Invalid feaURLWhitelist: ${error.message}`);
		}
	}

	// Log a message in logger if this was a restricted url
	cancel && logger.warn(`Restricted access to url: ${url}`);
	// We are done, continue or cancel request
	callback({
		cancel,
		// redirectURL: cancel ? 'http://localhost:3375/components/welcome/welcome.html' : null
	});
};

export const setupListeners = (
	app: App,
	system: MainSystem,
	manifest: Manifest,
	protocolHandler: ProtocolHandler,
	downloadManager: DownloadManager,
	splashScreen: SplashScreen
) => {
	/**
	 * Start the WindowEventStream event handler. This powers the window service.
	 */
	listenForWindowStreamSubscriber();

	/**
	 * Sets up a handler for when a client certificate is requested from a server resource.
	 * See renderer/System.addSelectClientCertificateListener() for the algorithm.
	 */
	mainBus.addHandler("select-client-certificate", (eventObj) => {
		const { id } = eventObj.data;
		logger.info("Subscribing to select-client-certificate event");
		app.on("select-client-certificate", (event, webContents, url, list, callback) => {
			logger.info("select-client-certificate event fired");
			// Prevent Electron from using the first client certificate from the store
			event.preventDefault();

			// Send list of client certificates and the server url back to the renderer process
			// to make a choice
			mainBus.sendEvent(`pick-client-certificate.${id}`, {
				url,
				list,
			});

			// Add a one time handler to receive the choice
			const unsubscribe = mainBus.addHandler(`picked-client-certificate.${id}`, (evtObj) => {
				const { certificate } = evtObj.data;
				logger.info("Choice received for picked-client-certificate event");
				// Return the choice to Electron
				callback(certificate);
				unsubscribe();
			});
		});

		eventObj.respond();
	});

	/**
	 * Sets up a handler for certificate errors. The handler (in the renderer process) can
	 * decide whether to proceed despite the error.
	 */
	mainBus.addHandler("certificate-error.subscribe", (eventObj) => {
		const { id } = eventObj.data;
		logger.info("Subscribing to certificate-error event");
		// Register with Electron that we'd like to have a say when there is a certificate error
		app.on("certificate-error", (event, webContents, url, error, certificate, callback) => {
			logger.info("certificate-error event fired");
			// Prevent Electron from simply denying the page
			event.preventDefault();

			// Ask the handler (in the renderer) whether or not to allow the site,
			// providing the information necessary to make that decision
			mainBus.sendEvent(`possibly-override-certificate-error.${id}`, {
				url,
				error,
				certificate,
			});

			// Add a one time handler to receive the response from the renderer
			const unsubscribe = mainBus.addHandler(`allow-or-disallow-page.${id}`, (evtObj) => {
				const { allowOrDisallow } = evtObj.data;
				logger.info("Response received for certificate-error event", allowOrDisallow);

				// Give our choice to Electron
				callback(allowOrDisallow);
				unsubscribe();
			});
		});

		eventObj.respond();
	});

	mainBus.addHandler("hideSplashScreen", splashScreen.hideSplashScreenFromBus);
	mainBus.addHandler("showSplashScreen", splashScreen.showSplashScreenFromBus);

	/**
	 * Sets up a handler to check when the LoggerService will be ready and sends FEA log to it.
	 */
	mainBus.addHandler("central-logger-ready", () => {
		logger.enableLoggingToCentralLogger((message: any) => {
			mainBus.sendEvent("systemEvent.log-fea", message);
		});
	});

	mainBus.addHandler("central-logger-state-changed", (event) => {
		logger.updateCentralLoggerState(event.data);
	});

	/**
	 * Provides identity to windows (BrowserWindows and BrowserViews) when they are launched.
	 *
	 * When the e2o.js preload runs in a new window, the first thing it does is make an identity
	 * request using ipcRenderer.sendSync(). The identity, which will already be established in
	 * the mainStore, contains the windowName, webContentsId and other information that
	 * the preload requires to initialize the window.
	 */
	ipcMain.on("fea.getWindowIdentity", getWindowIdentity);

	/**
	 * Windows may request "wraps" (handles) to other windows in order to manipulate them. The
	 * wrap needs to connect the windowName with the MainWindow's identity.
	 */
	ipcMain.on("fea.getWindowIdentityForWindowName", getWindowIdentityForWindowName);

	// Check all urls against feaURLWhiteList config
	session.defaultSession.webRequest.onBeforeRequest({ urls: [] }, (request: any, callback: Function) =>
		maybePreventLoadDueToWhiteList(manifest, request, callback)
	);

	/**
	 * When a browser makes a chrome permission request, checks to see what permissions the window has allowed/denied.
	 */
	session.defaultSession.setPermissionRequestHandler(async (webContents, permission, callback) => {
		// Get the window and check to see if it's allowed to do something (e.g., geolocation, notifications).
		const { mainWindow } = windowsIndex.byWebContentsId[webContents.id] ?? {};
		const permissionAllowed = permissionsManager.checkPermission(mainWindow, `Window.chromePermissions.${permission}`);
		if (!permissionAllowed) {
			logger.warn(`Permission disallowed. Permission: ${permission} URL: ${webContents.getURL()}`);
		}
		callback(permissionAllowed);
	});

	session.defaultSession.webRequest.onBeforeSendHeaders((details, callback) => {
		maybeModifyRequestHeaders(details, callback);
	});

	/**
	 * Modify any incoming headers if necessary.
	 */
	session.defaultSession.webRequest.onHeadersReceived((details, callback) => {
		maybeModifyResponseHeaders(manifest, details, callback);
	});

	/**
	 * When 400 - 500 errors are received for a web page we optionally log the error.
	 * We also re-enable the Electron titlebar for any window that would otherwise
	 * have received a Finsemble titlebar because without a titlebar, the end user would not
	 * be able to close the window.
	 */
	session.defaultSession.webRequest.onResponseStarted((details) => {
		try {
			const isHTTPError = details.statusCode >= 400 && details.statusCode <= 599;

			if (!isHTTPError) return;
			const shouldLogHTTPErrors = manifest["finsemble-electron-adapter"]?.logHTTPErrors ?? true;
			if (shouldLogHTTPErrors) {
				const { webContentsName } = mainStore.webContents[details.webContents?.id ?? -1] ?? {};
				const { statusCode, url } = details;
				logger.warn(`${statusCode} ${url}`, webContentsName);
			}
		} catch (e) {
			logger.error(`Unhandled exception in session.defaultSession.webRequest.onResponseStarted()`, e);
		}
	});

	session.defaultSession.webRequest.onErrorOccurred((details) => {
		const { url, error, referrer } = details;
		const errorMessage = `webRequest.onErrorOccurred ${url} in ${referrer}`;
		if (debugLevelErrors.includes(error)) {
			logger.debug(errorMessage, error);
		} else {
			logger.error(errorMessage, error);
		}
	});

	session.defaultSession.webRequest.onCompleted((details) => {
		maybeCloseAttachmentWindows(details);
	});

	// Close down if all of our windows are closed
	app.on("window-all-closed", () => {
		// This event is triggered when the last window closes. Depending on timing, the event may be triggered by the splash screen
		// if the main application hasn't started yet. Don't exit if the main application has not started yet.
		if (system.mainApplicationStarted) {
			logger.log("APPLICATION LIFECYCLE: All windows closed.");
			if (system.restarting) {
				logger.log("APPLICATION LIFECYCLE: Restarting FEA.");
				return;
			}
			system.exit();
		} else {
			logger.log(
				"APPLICATION LIFECYCLE: all windows closed event received before main application has started. This event may have been triggered by the splash screen closing."
			);
		}
	});

	/**
	 * Triggered when a download is initiated
	 */
	session.defaultSession.on("will-download", (event, item, webContents) => {
		logger.debug("will-download event received");
		downloadManager.handleNewDownloadItem(item);
	});

	/**
	 * Handle attempts to start a second instance of FEA, in order to intercept any protocol handler
	 * calls.
	 */
	app.on("second-instance", (event: any, commandLine: any) => {
		// The commandLine is the command that started a second instance.
		logger.debug(`Protocol client data received ${commandLine}`);
		/**
		 * The way the protocol handler is set up, our passed in argument will always be the last one in the array.
		 * https://ss64.com/nt/syntax-args.html
		 */
		protocolHandler.handleSecondInstance(commandLine.pop());
	});

	/**
	 * Following electron security checklist, this is a near-global catch to ensure webviews
	 * do not have preloads or node integration.
	 * See https://www.electronjs.org/docs/latest/tutorial/security#12-verify-webview-options-before-creation
	 * Note that because webviewTag is disabled by default, and as of this writing, we don't allow
	 * overriding webpreferences, this check should be extraneous.  However, in case the latter is changed,
	 * this will help with securing any created webviews.
	 */
	app.on("web-contents-created", (eventApp, contents) => {
		contents.on("will-attach-webview", (event, webPreferences, params) => {
			delete webPreferences.preload;
			webPreferences.nodeIntegration = false;
		});
	});

	/**
	 * Adds app 'certificate-error' event handler to
	 * intercept certificate errors when loading view content.
	 * Triggers a native notification for bad certificates.
	 */
	app.on("certificate-error", (event, _webContents, url, error, certificate, callback) => {
		logger.info("certificate-error event fired");

		const feaConfig = manifest["finsemble-electron-adapter"];
		if (feaConfig?.ignoreCertificateErrorHosts?.length) {
			const ignoredURLs = feaConfig["ignoreCertificateErrorHosts"] as string[];
			const ignoredHosts: string[] = [];
			ignoredURLs.forEach((_url) => {
				try {
					ignoredHosts.push(new URL(_url).host); // if ignored URL is real url
				} catch (e) {
					ignoredHosts.push(_url); // if ignored URL is host
				}
			});
			const { host } = new URL(url);
			if (ignoredHosts.includes(host)) {
				const message = `Trying to open site (${url}) with bad certificate (${error}). The host is trusted.`;
				logger.info(message);

				event.preventDefault();
				callback(true);
				return;
			}
		}

		const message = `Trying to open site (${url}) with bad certificate (${error}). Request will be rejected.`;
		logger.warn(message);

		system.toast(
			{
				title: manifest.startup_app.name || "Finsemble",
				body: message,
			},
			{
				devModeOnly: true,
			}
		);
		event.preventDefault();
		callback(false);
	});

	ipcMain.on("logger:message", (event, message) => {
		logger.onLog(event, message);
	});
	permissionsManager.addRestrictedListener(
		`-SpawnWindow`,
		async (eventObj: any) => {
			const fsa = await createWindowRequest(eventObj.data, manifest);
			eventObj.respond(fsa);
		},
		"Application.spawn"
	);

	permissionsManager.addRestrictedListener("clearCache", system.clearCache, "System.clearCache");
	permissionsManager.addRestrictedListener("getVersion", system.getVersion, "System.getVersion");
	permissionsManager.addRestrictedListener("getFEAInfo", system.getFEAInfo, "System.getFEAInfo");
	permissionsManager.addRestrictedListener(
		"startupApplicationHandshake",
		system.startupApplicationHandshake,
		"System.startupApplicationHandshake"
	);
	permissionsManager.addRestrictedListener(
		"openUrlWithBrowser",
		system.openUrlWithBrowser,
		"System.openUrlWithBrowser"
	);
	permissionsManager.addRestrictedListener("deleteAssets", system.deleteAssets, "System.deleteAssets");
	permissionsManager.addRestrictedListener("getHostSpecs", system.getHostSpecs, "System.getHostSpecs");
	permissionsManager.addRestrictedListener("getMonitorInfo", system.getMonitorInfo, "System.getMonitorInfo");
	permissionsManager.addRestrictedListener("flushStorage", system.flushStorage, "System.flushStorage");
	permissionsManager.addRestrictedListener("getMousePosition", system.getMousePosition, "System.getMousePosition");
	permissionsManager.addRestrictedListener(
		"launchExternalProcess",
		system.launchExternalProcess,
		"System.launchExternalProcess"
	);
	permissionsManager.addRestrictedListener(
		"restartApplication",
		system.restartApplication,
		"Application.restartApplication"
	);
	permissionsManager.addRestrictedListener("getProcessList", system.getProcessList, "System.getProcessList");
	permissionsManager.addRestrictedListener("notification", system.notificationBusHandler, "Notification.notification");
	permissionsManager.addRestrictedListener(
		"setTrayIconDeprecated",
		system.setTrayIconDeprecated,
		"Application.setTrayIcon"
	);
	permissionsManager.addRestrictedListener(
		"getDownloadList",
		downloadManager.getDownloadList,
		"System.getDownloadList"
	);
	permissionsManager.addRestrictedListener(
		"showDownloadedFileInFolder",
		downloadManager.showDownloadedFileInFolder,
		"System.showDownloadedFileInFolder"
	);
	permissionsManager.addRestrictedListener(
		"removeDownloadItem",
		downloadManager.removeDownloadItem,
		"System.removeDownloadItem"
	);
	permissionsManager.addRestrictedListener("cancelDownload", downloadManager.cancelDownload, "System.cancelDownload");
	permissionsManager.addRestrictedListener("pauseDownload", downloadManager.pauseDownload, "System.pauseDownload");
	permissionsManager.addRestrictedListener("resumeDownload", downloadManager.resumeDownload, "System.resumeDownload");
	permissionsManager.addRestrictedListener(
		"setDownloadSavePath",
		downloadManager.setDownloadSavePath,
		"System.setDownloadSavePath"
	);
	permissionsManager.addRestrictedListener(
		"clearDownloadList",
		downloadManager.clearDownloadList,
		"System.clearDownloadList"
	);
	permissionsManager.addRestrictedListener("setTrayIcon", system.setTrayIcon, "System.setTrayIcon");
	permissionsManager.addRestrictedListener("logToDisk", system.logToDisk, "System.logToDisk");
	permissionsManager.addRestrictedListener("removeTrayIcon", system.removeTrayIcon, "Application.removeTrayIcon");
	permissionsManager.addRestrictedListener(
		"getEnvironmentVariable",
		system.getEnvironmentVariable,
		"System.getEnvironmentVariable"
	);
	permissionsManager.addRestrictedListener("getProtocolString", system.getProtocolString, "System.getProtocolString");
	permissionsManager.addRestrictedListener(
		"availableFormatsFromClipboard",
		system.availableFormatsFromClipboard,
		"System.availableFormats"
	);
	permissionsManager.addRestrictedListener("writeTextToClipboard", system.writeTextToClipboard, "System.writeText");
	permissionsManager.addRestrictedListener("writeHTMLToClipboard", system.writeHTMLToClipboard, "System.writeHTML");
	permissionsManager.addRestrictedListener("writeRTFToClipboard", system.writeRTFToClipboard, "System.writeRTF");
	permissionsManager.addRestrictedListener("writeToClipboard", system.writeToClipboard, "System.write");
	permissionsManager.addRestrictedListener("readTextFromClipboard", system.readTextFromClipboard, "System.readText");
	permissionsManager.addRestrictedListener("readHTMLFromClipboard", system.readHTMLFromClipboard, "System.readHTML");
	permissionsManager.addRestrictedListener("readRTFFromClipboard", system.readRTFFRomClipboard, "System.readRTF");
	permissionsManager.addRestrictedListener(
		"getUpdateAvailable",
		system.getUpdateAvailable,
		"System.getUpdateAvailable"
	);
	permissionsManager.addRestrictedListener(
		"getCommandLineArguments",
		system.getCommandLineArguments,
		"System.getCommandLineArguments"
	);
	permissionsManager.addRestrictedListener("registerGlobalShortcut", system.registerGlobalShortcut, "System.register");
	permissionsManager.addRestrictedListener(
		"isRegisteredGlobalShortcut",
		system.isRegisteredGlobalShortcut,
		"System.isRegistered"
	);
	permissionsManager.addRestrictedListener(
		"unregisterGlobalShortcut",
		system.unregisterGlobalShortcut,
		"System.unregister"
	);
	mainBus.addHandler("checkPermission", system.checkPermission); // This listener can not be disabled as other permission checking is dependent on it.

	const sendMonitorInfoChangedEvent = (reason: string) => {
		MonitorInfo.getMonitorInfo((monitorInfo: any) => {
			mainBus.sendEvent("systemEvent.monitor-info-changed", {
				...monitorInfo,
				taskbar: monitorInfo.primaryMonitor?.taskbar ?? {},
				reason,
				topic: "system",
				type: "monitor-info-changed",
			});
		});
	};

	screen.addListener("display-added", () => {
		logger.log("APPLICATION LIFECYCLE: Monitor added.");
		sendMonitorInfoChangedEvent("display-added");
	});

	screen.addListener("display-removed", () => {
		logger.log("APPLICATION LIFECYCLE: Monitor removed.");
		sendMonitorInfoChangedEvent("display-removed");
	});

	screen.addListener("display-metrics-changed", (event, display) => {
		logger.log("APPLICATION LIFECYCLE: Monitor display metrics changed.", JSON.stringify(display));
		sendMonitorInfoChangedEvent("display-metrics-changed");
	});

	powerMonitor.on("lock-screen", () => {
		logger.log("APPLICATION LIFECYCLE: System locked.");
		mainBus.sendEvent("systemEvent.session-changed");
	});

	powerMonitor.on("unlock-screen", () => {
		logger.log("APPLICATION LIFECYCLE: System unlocked.");
		mainBus.sendEvent("systemEvent.session-changed");
	});
	permissionsManager.addRestrictedListener(
		`-getManifest`,
		(eventObj) => {
			eventObj.respond(manifest);
		},
		"System.getManifest"
	);
	permissionsManager.addRestrictedListener(`-terminate`, system.exit, "System.exit");

	permissionsManager.addRestrictedListener(
		"getScreenShareSources",
		ScreenCapture.getScreenShareSources,
		"System.getScreenShareSources"
	);
};
